import {
  Button,
  Grid,
  Stack,
  MenuItem,
  Select,
  FormControl,
  TextField,
  Typography,
  DialogContentText,
  Box,
  InputLabel,
  FormHelperText,
  Alert,
  Chip,
  Link as MuiLink,
} from "@mui/material";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useFetch } from "../../../../services/hooks/useFetch";
import { apiPrefix } from "../../../../utils/main";
import { HTTPMethod } from "@fusionauth/typescript-client";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { schemaIdentifier } from "../../../../models/Source";
import CustomDialog from "../../../../components/CustomDialog";
import { useSearchParams } from "react-router";
import {
  DataGridPremium,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { TableNoRowsCustomization } from "../../../../components/datagrid/TableNoRowsCustomization";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";

const flowsheetListUrl = `/settings?settings=flowsheet-groups`;
export default function FlowsheetGroupDetails({
  flowsheetGroup,
}: {
  flowsheetGroup: FlowsheetGroup | Partial<FlowsheetGroup> | undefined;
}) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const [name, setName] = useState(flowsheetGroup?.name);
  const [schema, setSchema] = useState(
    flowsheetGroup?.sourceSchemaIdentifier ?? schemaIdentifier.mayo_dug_216,
  );
  const [sourceDks, setSourceDks] = useState(
    flowsheetGroup?.groupDks?.join(", "),
  );
  const [nameError, setNameError] = useState(false);
  const [schemaError, setSchemaError] = useState(false);
  const [sourceDksError, setSourceDksError] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const { fetch: fetchUsedProjects, data: usedProjects } =
    useFetch<Project[]>();

  const {
    create: createFlowsheetGroup,
    update: updateFlowsheetGroup,
    delete: deleteFlowsheetGroup,
    error: flowsheetGroupError,
  } = useFetch<FlowsheetGroup, Partial<FlowsheetGroup>>();

  const columns: GridColDef[] = [
    {
      field: "company",
      width: 200,
      headerName: "Company",
      filterable: false,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Project, unknown, unknown>) => {
        return params.row.company?.name || "";
      },
    },
    {
      field: "name",
      width: 200,
      headerName: "Project Name",
      filterable: false,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "statusName",
      headerName: "Status",
      editable: false,
      flex: 0.5,
      minWidth: 150,
      headerClassName: "project-table-header",
      renderCell: (params) => {
        const project = params.row;
        return project.isActive ? (
          <Chip label={project.statusName} size="small" />
        ) : (
          <Chip label={"Inactive"} color={"error"} size="small" />
        );
      },
    },
    {
      field: "locked",
      headerName: "Permissions",
      editable: false,
      flex: 0.5,
      minWidth: 150,
      headerClassName: "project-table-header",
      cellClassName: (params) =>
        params.row.isLocked ? "project-table-permissions-locked" : "",
      valueGetter: (value) => {
        return value ? "Locked" : "Unlocked";
      },
      type: "singleSelect",
      valueOptions: ["Locked", "Unlocked"],
      renderCell: (params) => {
        const project = params.row;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: "0.25rem",
            }}
          >
            {project.isLocked ? (
              <LockOutlinedIcon fontSize="small" />
            ) : (
              <LockOpenOutlinedIcon fontSize="small" />
            )}
            <MuiLink
              component={Link}
              to={`/settings?projectId=${project.id}&settings=project-permissions`}
              variant="body2"
            >
              {project.isLocked ? "Locked" : "Unlocked"}
            </MuiLink>
          </div>
        );
      },
    },
  ];

  function deleteClicked() {
    void deleteFlowsheetGroup({
      path: `${apiPrefix}/flowsheets/${flowsheetGroup?.id}`,
      method: HTTPMethod.DELETE,
      postResponseAction: () => {
        if (!flowsheetGroupError) {
          window.location.href = flowsheetListUrl;
        }
      },
    });
  }

  function saveClicked() {
    const flowsheetGroupToSave: Partial<FlowsheetGroup> = flowsheetGroup
      ? flowsheetGroup
      : {};
    flowsheetGroupToSave.name = name;
    flowsheetGroupToSave.groupDks = sourceDks
      ?.split(",")
      .map((dk) => dk.trim())
      .filter((dk) => dk !== "");
    flowsheetGroupToSave.sourceSchemaIdentifier = schema;

    if (hasValidationErrors()) {
      return;
    }

    if (flowsheetGroupToSave.id) {
      void updateFlowsheetGroup({
        path: `${apiPrefix}/flowsheets/${flowsheetGroup?.id}`,
        body: flowsheetGroupToSave,
        method: HTTPMethod.PATCH,
        postResponseAction: () => {
          if (!flowsheetGroupError) {
            window.location.href = flowsheetListUrl;
          }
        },
      });
    } else {
      void createFlowsheetGroup({
        path: `${apiPrefix}/flowsheets`,
        body: flowsheetGroupToSave,
        method: HTTPMethod.POST,
        postResponseAction: () => {
          if (!flowsheetGroupError) {
            window.location.href = flowsheetListUrl;
          }
        },
      });
    }
  }

  function cancelClicked() {
    window.location.href = flowsheetListUrl;
  }

  function hasValidationErrors(): boolean {
    let hasErrors = false;

    if (!name || name === "") {
      setNameError(true);
      hasErrors = true;
    } else {
      setNameError(false);
    }

    if (!schema || schema === "") {
      setSchemaError(true);
      hasErrors = true;
    } else {
      setSchemaError(false);
    }

    if (!sourceDks || sourceDks.length === 0) {
      setSourceDksError(true);
      hasErrors = true;
    } else {
      setSourceDksError(false);
    }

    return hasErrors;
  }

  function handleAlertClose() {
    setFailureReason("");
  }

  useEffect(() => {
    if (flowsheetGroupError && flowsheetGroupError !== "") {
      setFailureReason(JSON.stringify(flowsheetGroupError));
    }
  }, [flowsheetGroupError]);

  useEffect(() => {
    if (!usedProjects && flowsheetGroup?.id) {
      void fetchUsedProjects(
        `${apiPrefix}/flowsheets/${flowsheetGroup?.id}/usages`,
      );
    }
  }, [usedProjects, flowsheetGroup?.id, fetchUsedProjects]);

  function CustomNoRowsMessage() {
    return (
      <TableNoRowsCustomization
        icon={
          <PendingOutlinedIcon
            style={{
              color: "#0D3E56",
              width: "32px",
              height: "32px",
              marginLeft: "18px",
            }}
          />
        }
        text={"This Flowsheet Data Group has not been used in any projects."}
        iconBackground={48}
      />
    );
  }

  return (
    <div
      style={{
        height: "100%",
        minHeight: "800px",
        width: "800px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CustomDialog
        open={failureReason !== ""}
        handleClose={handleAlertClose}
        title={"Flowsheet creation failed"}
      >
        <DialogContentText>{`Flowsheet Group creation failed with the following reason: ${failureReason}`}</DialogContentText>
      </CustomDialog>
      <Grid>
        <Typography variant="h4" textAlign="left">
          {flowsheetGroup?.name ? flowsheetGroup.name : "New Flowsheet Group"}
        </Typography>
      </Grid>
      <br />
      <Grid>
        <Typography variant="body2">Manage Flowsheet groups</Typography>
      </Grid>
      <br />
      <Grid>
        <Typography variant="body2">* Required fields</Typography>
      </Grid>
      <br />

      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
        <Grid size={{ xs: 4, sm: 8, md: 12 }}>
          <FormControl fullWidth error={nameError}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Group Name"
              placeholder="Enter a descriptive group name up to 100 characters"
              value={name ?? ""}
              onChange={(event) => {
                setName(event.target.value);
              }}
              error={nameError}
              helperText={nameError ? "This field is required" : ""}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 4, sm: 8, md: 12 }}>
          <FormControl required fullWidth error={schemaError}>
            <InputLabel id="schema-label" required>
              Schema
            </InputLabel>

            <Select
              labelId="schema-label"
              label="Schema"
              required
              variant="outlined"
              aria-placeholder="Choose a data schema"
              value={schema}
              onChange={(event) => {
                setSchema(event.target.value);
              }}
              error={schemaError}
            >
              <MenuItem value={schemaIdentifier.mayo_dug_216}>
                MAYO DUG 2.16
              </MenuItem>
            </Select>
            {schemaError && (
              <FormHelperText>This field is required!</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid size={{ xs: 4, sm: 8, md: 12 }}>
          <FormControl fullWidth error={sourceDksError}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Source DK List"
              placeholder="Enter a comma separated list of values"
              value={sourceDks ?? ""}
              multiline={true}
              minRows={5}
              onChange={(event) => {
                setSourceDks(event.target.value);
              }}
              error={sourceDksError}
              helperText={sourceDksError ? "This field is required" : ""}
            />
          </FormControl>
        </Grid>
      </Grid>
      <br />

      {mode === "modify" &&
        flowsheetGroup?.projectFlowsheetGroups?.length !== 0 && (
          <>
            <Grid>
              <Alert severity="info">
                Once a Flowsheet Data Group has been used in one or more
                projects, it can no longer be deleted or edited.
              </Alert>
            </Grid>
          </>
        )}

      {mode === "modify" && (
        <>
          <br />
          <Grid>
            <DataGridPremium
              className="custom-groups-table"
              columns={columns}
              rows={usedProjects ? usedProjects : []}
              rowSelection={false}
              sx={usedProjects?.length === 0 ? { height: "348px" } : {}}
              slots={{ noRowsOverlay: CustomNoRowsMessage }}
            />
          </Grid>
        </>
      )}

      <Stack direction="row" spacing={1}>
        {flowsheetGroup?.id && !flowsheetGroup?.inUse && (
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            size="small"
            variant="contained"
            onClick={() => deleteClicked()}
          >
            Delete Group
          </Button>
        )}
        <Box flexGrow={1} />
        <Button variant="outlined" onClick={() => cancelClicked()}>
          Cancel
        </Button>

        {!flowsheetGroup?.inUse && (
          <Button
            startIcon={<SaveIcon />}
            size="small"
            variant="contained"
            onClick={() => saveClicked()}
          >
            Save Group
          </Button>
        )}
      </Stack>
    </div>
  );
}
